import React, { useMemo } from 'react';
import { Card, Progress, Row, Col, Empty } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { Link } from 'react-router-dom';

import { StyledTable } from '../entity/shared/components/styled/StyledTable';
import { CustomAvatar } from '../shared/avatar';

import { useAggregateOwnersForWidgetQuery } from '../../graphql/search.generated';
import { EntityType, FacetFilterInput } from '../../types.generated';

import { useTranslation } from '../../i18n/useTranslation';
import { useEntityRegistry } from '../useEntityRegistry';
import { formatNumber } from '../shared/formatNumber';
import { WidgetCardHeader } from './WidgetCardHeader';
import { EntityNameLink } from './EntityNameLink';

import { PROGRESS_TRAIL_COLOR, PROGRESS_STROKE_COLOR } from '../shared/charts/colorUtilService';
import { DOMAINS_FILTER_NAME, OWNERS_FILTER_NAME, URN_FILTER_NAME } from '../search/utils/constants';
import { COMMON_ASSET_ENTITY_TYPES, getFacetAggregations } from './utils';
import { getFieldValues, useDashboardFilters } from './dashboardFilters';

interface OwnerRowData {
    id: number;
    urn: string | undefined;
    ownerName: string;
    photoUrl: string | undefined;
    isGroup: boolean;
    entityType: EntityType;
    assets: string;
    count: number;
}

// The owner entity fields read off an aggregation (CorpUser/CorpGroup), narrowed structurally.
interface OwnerEntityShape {
    urn?: string;
    type?: EntityType;
    username?: string;
    name?: string;
    editableProperties?: { displayName?: string | null; pictureLink?: string | null } | null;
    properties?: { displayName?: string | null; fullName?: string | null } | null;
}

export const AssetsPerOwnerWidget = () => {
    const { t } = useTranslation();
    const entityRegistry = useEntityRegistry();
    const dashboardFilters = useDashboardFilters();

    const selectedOwnerUrns = getFieldValues(dashboardFilters, OWNERS_FILTER_NAME);

    // Per spec: domain selections map to a `urn` clause, alongside `owners`.
    const ownerOrFilters = useMemo(() => {
        const urnValues = getFieldValues(dashboardFilters, DOMAINS_FILTER_NAME);
        const owners = selectedOwnerUrns;

        const clauses: FacetFilterInput[] = [];
        if (urnValues.length) clauses.push({ field: URN_FILTER_NAME, values: urnValues });
        if (owners.length) clauses.push({ field: OWNERS_FILTER_NAME, values: owners });

        return clauses.length ? [{ and: clauses }] : undefined;
    }, [dashboardFilters, selectedOwnerUrns]);

    const { data: assetsPerOwnerData, loading: assetsPerOwnerLoading } = useAggregateOwnersForWidgetQuery({
        variables: {
            input: {
                query: '*',
                facets: [OWNERS_FILTER_NAME],
                types: COMMON_ASSET_ENTITY_TYPES,
                orFilters: ownerOrFilters,
            },
        },
        fetchPolicy: 'no-cache',
    });

    const assetsOwnerDataTransformed = useMemo<OwnerRowData[]>(() => {
        const ownerAggregations = getFacetAggregations(
            assetsPerOwnerData?.aggregateAcrossEntities?.facets,
            OWNERS_FILTER_NAME,
        );

        if (ownerAggregations.length === 0) {
            return [];
        }

        return ownerAggregations
            .sort((a, b) => b.count - a.count)
            .map((agg, index) => {
                const entity = agg.entity as OwnerEntityShape | null | undefined;
                const isGroup = entity?.type === EntityType.CorpGroup;
                const ownerName =
                    entity?.editableProperties?.displayName ||
                    entity?.properties?.displayName ||
                    entity?.properties?.fullName ||
                    entity?.username ||
                    entity?.name ||
                    agg.value.split(':').pop() ||
                    'Unknown';
                const photoUrl = entity?.editableProperties?.pictureLink || undefined;
                const entityType = isGroup ? EntityType.CorpGroup : EntityType.CorpUser;

                return {
                    id: index,
                    urn: entity?.urn,
                    ownerName,
                    photoUrl,
                    isGroup,
                    entityType,
                    assets: formatNumber(agg.count),
                    count: agg.count,
                };
            });
    }, [assetsPerOwnerData]);

    const maxCount = assetsOwnerDataTransformed[0]?.count ?? 1;

    const assetsOwnerGrid = useMemo<ColumnType<OwnerRowData>[]>(
        () => [
            {
                width: '50%',
                title: t('Owner'),
                dataIndex: 'ownerName',
                key: 'ownerName',
                render: (ownerName: string, record: OwnerRowData) => (
                    <Row align="middle" gutter={8}>
                        <Col flex="none">
                            {record.urn ? (
                                <Link to={entityRegistry.getEntityUrl(record.entityType, record.urn)}>
                                    <CustomAvatar
                                        name={ownerName}
                                        photoUrl={record.photoUrl}
                                        isGroup={record.isGroup}
                                        hideTooltip
                                    />
                                </Link>
                            ) : (
                                <CustomAvatar
                                    name={ownerName}
                                    photoUrl={record.photoUrl}
                                    isGroup={record.isGroup}
                                    hideTooltip
                                />
                            )}
                        </Col>
                        <Col flex="auto">
                            {record.urn ? (
                                <EntityNameLink
                                    name={ownerName}
                                    to={entityRegistry.getEntityUrl(record.entityType, record.urn)}
                                />
                            ) : (
                                <div>{ownerName}</div>
                            )}
                        </Col>
                    </Row>
                ),
            },
            {
                width: '30%',
                dataIndex: 'count',
                key: 'count',
                render: (count: number) => {
                    const percent = Math.round((count / maxCount) * 100);
                    return (
                        <Progress
                            size="small"
                            percent={percent}
                            strokeWidth={4}
                            showInfo={false}
                            trailColor={PROGRESS_TRAIL_COLOR}
                            strokeColor={PROGRESS_STROKE_COLOR}
                        />
                    );
                },
            },
            {
                width: '20%',
                title: t('Assets'),
                align: 'right' as const,
                dataIndex: 'assets',
                key: 'assets',
            },
        ],
        [maxCount, t, entityRegistry],
    );

    return (
        <Card
            className="f-bordered f-dashboard-grid-card"
            title={<WidgetCardHeader title={t('Assets per Owner')} loading={assetsPerOwnerLoading} />}
        >
            <StyledTable<OwnerRowData>
                rowKey="id"
                size="small"
                dataSource={assetsOwnerDataTransformed}
                columns={assetsOwnerGrid}
                locale={{ emptyText: <Empty description={t('NO_DATA_AVAILABLE')} /> }}
                pagination={{
                    pageSize: 5,
                    showSizeChanger: false,
                    showTotal: (total, range) => `${range[0]} - ${range[1]} of ${total}`,
                }}
            />
        </Card>
    );
};
